import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Heart, MapPin, Truck, Store, ShoppingBag, Loader2, Star } from "lucide-react";
import api, { imageUrl, money } from "@/lib/api";
import { useBasket } from "@/context/BasketContext";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "@/context/LocationContext";
import { Stars } from "@/components/Stars";
import { ProductCard } from "@/components/ProductCard";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useBasket();
  const { user } = useAuth();
  const { loc } = useLocation();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);

  const load = () => {
    const geo = loc ? `?lat=${loc.lat}&lng=${loc.lng}` : "";
    api.get(`/products/${id}${geo}`).then((r) => { setP(r.data); setActiveImg(0); }).catch(() => navigate("/browse"));
  };
  useEffect(load, [id, loc]);

  if (!p) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#1A3C2A]" /></div>;

  const images = p.images?.length ? p.images : [""];
  const soldOut = p.available === false || p.stock <= 0;

  const submitReview = async () => {
    if (!user) { toast.error("Sign in to leave a review"); return; }
    try {
      await api.post("/reviews", { product_id: p.id, rating: reviewRating, comment: reviewText });
      toast.success("Review added");
      setReviewText(""); load();
    } catch { toast.error("Could not add review"); }
  };

  const buyNow = () => { add(p, qty); navigate("/basket"); };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        <div>
          <div className="aspect-square rounded-3xl overflow-hidden bg-[#F4EFE6] border border-[#E5DEC9]">
            <img data-testid="product-main-image" src={imageUrl(images[activeImg])} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                  className={`w-16 h-16 rounded-xl overflow-hidden border-2 ${activeImg === i ? "border-[#E07A5F]" : "border-[#E5DEC9]"}`}>
                  <img src={imageUrl(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <Link to={`/seller/view/${p.seller_id}`} data-testid="product-seller-link" className="text-sm font-semibold text-[#E07A5F] hover:underline">{p.seller_name}</Link>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A3C2A] mt-1">{p.name}</h1>
          <div className="flex items-center gap-4 mt-2">
            <Stars rating={p.rating} count={p.review_count} />
            <span className="text-sm text-[#5A626A] flex items-center gap-1"><MapPin size={13} /> {p.seller_area?.split(",")[0]}{p.distance_miles != null ? ` · ${p.distance_miles} mi` : ""}</span>
          </div>
          <p className="font-display text-3xl font-extrabold text-[#1A3C2A] mt-4">{money(p.price)}</p>
          <p className="text-sm text-[#5A626A] mt-4 leading-relaxed whitespace-pre-line">{p.description}</p>

          <div className="mt-4 flex items-center gap-2">
            <span className={`text-sm font-semibold px-3 py-1 rounded-full ${soldOut ? "bg-red-50 text-red-600" : "bg-[#1A3C2A]/10 text-[#1A3C2A]"}`}>
              {soldOut ? "Out of stock" : `${p.stock} in stock`}
            </span>
          </div>

          {!soldOut && (
            <div className="flex items-center gap-4 mt-5">
              <div className="flex items-center border border-[#E5DEC9] rounded-full bg-white">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3"><Minus size={16} /></button>
                <span data-testid="qty-value" className="w-10 text-center font-semibold">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(Math.min(p.stock, qty + 1))} className="p-3"><Plus size={16} /></button>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mt-5">
            <Button data-testid="add-to-basket-button" disabled={soldOut} onClick={() => add(p, qty)}
              className="flex-1 rounded-full bg-[#1A3C2A] hover:bg-[#132E20] h-12 font-semibold gap-2">
              <ShoppingBag size={18} /> Add to Basket
            </Button>
            <Button data-testid="buy-now-button" disabled={soldOut} onClick={buyNow}
              className="flex-1 rounded-full bg-[#E07A5F] hover:bg-[#D06447] h-12 font-semibold">Buy Now</Button>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-5">
            <div className="flex items-center gap-2 text-sm text-[#5A626A] bg-white border border-[#E5DEC9] rounded-xl p-3">
              <Truck size={18} className="text-[#1A3C2A]" /> Local delivery available
            </div>
            <div className="flex items-center gap-2 text-sm text-[#5A626A] bg-white border border-[#E5DEC9] rounded-xl p-3">
              <Store size={18} className="text-[#1A3C2A]" /> Collection in person
            </div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-12">
        <h2 className="font-display text-xl font-bold text-[#1A3C2A] mb-4">Reviews ({p.review_count})</h2>
        {user && (
          <div className="bg-white border border-[#E5DEC9] rounded-2xl p-4 mb-5">
            <div className="flex items-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} data-testid={`review-star-${i}`} onClick={() => setReviewRating(i)}>
                  <Star size={22} className={i <= reviewRating ? "fill-[#E07A5F] text-[#E07A5F]" : "text-[#D8CFBC]"} />
                </button>
              ))}
            </div>
            <Textarea data-testid="review-text" value={reviewText} onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience..." className="rounded-xl" />
            <Button data-testid="submit-review" onClick={submitReview} className="mt-2 rounded-full bg-[#1A3C2A] hover:bg-[#132E20]">Post review</Button>
          </div>
        )}
        <div className="space-y-3">
          {p.reviews?.length === 0 && <p className="text-sm text-[#5A626A]">No reviews yet. Be the first!</p>}
          {p.reviews?.map((r) => (
            <div key={r.id} className="bg-white border border-[#E5DEC9] rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-[#22252A]">{r.user_name}</span>
                <Stars rating={r.rating} showNumber={false} />
              </div>
              {r.comment && <p className="text-sm text-[#5A626A] mt-1">{r.comment}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* Related */}
      {p.related?.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-xl font-bold text-[#1A3C2A] mb-4">Related products</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
            {p.related.map((rp, i) => <ProductCard key={rp.id} product={rp} index={i} />)}
          </div>
        </section>
      )}
      <div className="mt-16"><Footer /></div>
    </div>
  );
}
